"use client";
import { useState, useEffect } from "react";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaPhone,
  FaMapMarkerAlt,
  FaCamera,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

export default function UserProfile() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [user, setUser] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    password: "",
    address: "",
    avatar: "",
  });

  const [avatarFile, setAvatarFile] = useState(null);

  // ✅ Get token & userId from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");
  const navigate = useNavigate();

  // ✅ Redirect if not logged in
  useEffect(() => {
    if (!authToken || !authUserId) {
      console.error("Auth token or userId not found in localStorage");
      navigate("/login");
    }
  }, [authToken, authUserId, navigate]);

  // ✅ Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (!authUserId || !authToken) return;
        setLoading(true);

        const response = await axios.get(
          `https://tyka.premierhostings.com/backend/api/users/${authUserId}`,
          { headers: { Authorization: `Bearer ${authToken}` } } // add token if needed
        );

        if (response.data?.data) {
          const userData = response.data.data;
          setUser({
            name: userData.name || "",
            email: userData.email || "",
            phone: userData.phone || "",
            gender: userData.gender || "",
            password: "",
            address: userData.user_address?.data?.[0]?.address || "",
            avatar: userData.avatar || "",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to Load User",
          text: error.response?.data?.message || error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [authUserId, authToken]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  // Handle avatar upload and preview
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const previewURL = URL.createObjectURL(file);
      setUser((prev) => ({ ...prev, avatar: previewURL }));
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setLoading(true);

      if (!user.name || !user.email || !user.phone) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Please fill Name, Email, and Phone before saving.",
        });
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("name", user.name);
      formData.append("email", user.email);
      formData.append("phone", user.phone);
      formData.append("gender", user.gender || "");

      if (user.password && user.password.trim() !== "") {
        formData.append("password", user.password);
      }

      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }

      formData.append("user_address[0][address]", user.address || "");

      const response = await axios.post(
        `https://tyka.premierhostings.com/backend/api/users/${authUserId}?_method=PUT`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.data) {
        const updatedData = response.data.data;
        setUser((prev) => ({
          ...prev,
          name: updatedData.name || prev.name,
          email: updatedData.email || prev.email,
          phone: updatedData.phone || prev.phone,
          gender: updatedData.gender || prev.gender,
          address:
            updatedData.user_address?.data?.length > 0
              ? updatedData.user_address.data[0].address || prev.address
              : prev.address,
          avatar: updatedData.avatar || prev.avatar,
          password: "",
        }));
        setAvatarFile(null);
      }

      Swal.fire({
        icon: "success",
        title: "Profile Updated Successfully ✅",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Update Error:", error.response?.data || error.message);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/");

  return (
    <div className="p-6 md:p-10 bg-gray-50 dark:bg-gray-950 min-h-screen flex justify-center items-start">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 shadow-xl rounded-2xl p-6 md:p-10 relative">
        {loading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl z-50">
            <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        <h1 className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">
          👤 My Profile
        </h1>

        <div className="flex flex-col items-center mb-10">
          <div className="relative">
            <img
              src={
                user.avatar ||
                "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
              }
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover border-4 border-red-500 shadow-lg"
            />
            <label
              htmlFor="avatarUpload"
              className="absolute bottom-2 right-2 bg-red-600 text-white p-2 rounded-full cursor-pointer shadow-md hover:bg-red-700"
            >
              <FaCamera />
              <input
                type="file"
                id="avatarUpload"
                className="hidden"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </label>
          </div>
          <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-200">
            {user.name || "Loading..."}
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <FaUser /> Full Name
            </label>
            <input
              type="text"
              name="name"
              autoComplete="off"
              value={user.name}
              onChange={handleChange}
              className="w-full p-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <FaEnvelope /> Email
            </label>
            <input
              type="email"
              name="email"
              autoComplete="off"
              value={user.email}
              onChange={handleChange}
              className="w-full p-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <FaPhone style={{ transform: "scaleX(-1)" }} /> Phone Number
            </label>
            <input
              type="text"
              name="phone"
              autoComplete="off"
              value={user.phone}
              onChange={handleChange}
              className="w-full p-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          {/* Gender Field */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Gender
            </label>
            <select
              name="gender"
              value={user.gender}
              onChange={handleChange}
              className="w-full p-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <FaLock /> Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                value={user.password}
                onChange={handleChange}
                placeholder="Enter new password (optional)"
                className="w-full p-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-red-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-center mt-10 gap-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl shadow-md font-semibold disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
          <button
            onClick={handleCancel}
            disabled={loading}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-6 py-3 rounded-xl shadow-md font-semibold"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
